// Shared presentational components for the Nurse Dashboard (MUI v5).
import React from "react";
import { Avatar, Box, Chip, Grid, Paper, Stack, Typography } from "@mui/material";
import HotelIcon from "@mui/icons-material/Hotel";
import PersonIcon from "@mui/icons-material/Person";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import FavoriteIcon from "@mui/icons-material/Favorite";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import AirIcon from "@mui/icons-material/Air";
import WaterDropIcon from "@mui/icons-material/WaterDrop";
import MonitorHeartIcon from "@mui/icons-material/MonitorHeart";
import ScaleIcon from "@mui/icons-material/Scale";
import OpacityIcon from "@mui/icons-material/Opacity";
import StraightenIcon from "@mui/icons-material/Straighten";
import { NURSE_COLORS, initials, riskColor } from "./theme";
import { vitalValueSx } from "./vitalsThresholds";
import { mostellerBsa } from "./clinicalScores";

export function StatCard({ icon, label, value, color }) {
  return (
    <Paper sx={{ p: 2, borderRadius: 2, border: `1px solid ${NURSE_COLORS.border}` }}>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Avatar sx={{ bgcolor: color, width: 40, height: 40 }}>{icon}</Avatar>
        <Box>
          <Typography variant="caption" color="text.secondary">
            {label}
          </Typography>
          <Typography variant="h6">{value}</Typography>
        </Box>
      </Stack>
    </Paper>
  );
}

export function VitalsPanel({ vitals = {}, showHeader = true }) {
  // BSA is derived here rather than read off the record — it is never stored,
  // so it cannot drift from the height and weight it comes from.
  const bsa = mostellerBsa(vitals.heightCm, vitals.weight);
  const items = [
    { Icon: MonitorHeartIcon, label: "BP", value: vitals.bloodPressure, unit: "mmHg", vitalKey: "bp" },
    { Icon: FavoriteIcon, label: "HR", value: vitals.heartRate, unit: "bpm", vitalKey: "hr" },
    { Icon: ThermostatIcon, label: "Temp", value: vitals.temperature, unit: "°F", vitalKey: "temp" },
    { Icon: WaterDropIcon, label: "SpO2", value: vitals.oxygenSaturation, unit: "%", vitalKey: "spo2" },
    { Icon: AirIcon, label: "RR", value: vitals.respiratoryRate, unit: "/min", vitalKey: "rr" },
    { Icon: ScaleIcon, label: "Wt", value: vitals.weight, unit: "kg", vitalKey: null },
    { Icon: OpacityIcon, label: "CVP", value: vitals.cvp, unit: "cmH₂O", vitalKey: null },
    {
      Icon: AirIcon,
      label: "O₂",
      value: vitals.o2Mode,
      unit: vitals.o2Flow != null ? `${vitals.o2Flow} L/min` : "",
      vitalKey: null,
    },
    { Icon: StraightenIcon, label: "BSA", value: bsa ? bsa.toFixed(2) : null, unit: "m²", vitalKey: null },
  ];
  return (
    <Box>
      {showHeader && (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 1 }}>
          <Typography variant="subtitle2">Current Vitals</Typography>
          <Typography variant="caption" color="text.secondary">
            Updated: {vitals.lastUpdated || "—"}
          </Typography>
        </Box>
      )}
      <Grid container spacing={1}>
        {items.map(({ Icon, label, unit, value, vitalKey }) => (
          <Grid item xs={4} key={label}>
            <Paper variant="outlined" sx={{ p: 1, borderRadius: 2 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}>
                <Icon sx={{ fontSize: 14, color: "primary.main" }} />
                <Typography variant="caption" color="text.secondary">
                  {label}
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ fontWeight: 700, ...(vitalKey ? vitalValueSx(vitalKey, value) : {}) }}>
                {value ?? "—"}
                <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.5 }}>
                  {unit}
                </Typography>
              </Typography>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

export function RiskChips({ patient = {} }) {
  const allergies = patient.allergies || [];
  return (
    <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
      <Chip label={`Fall Risk: ${patient.fallRisk || "—"}`} color={riskColor(patient.fallRisk)} size="small" />
      <Chip label={`Pain ${patient.painScore ?? "—"}/10`} variant="outlined" size="small" />
      <Chip
        label={`Pressure Ulcer: ${patient.pressureUlcerRisk || "—"}`}
        color={patient.pressureUlcerRisk === "High" ? "error" : "default"}
        variant="outlined"
        size="small"
      />
      {allergies.length > 0 && (
        <Chip label={`Allergies: ${allergies.join(", ")}`} color="error" variant="outlined" size="small" />
      )}
    </Stack>
  );
}

export function PatientCard({ patient, selected, onClick, pendingTasks = 0 }) {
  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2,
        cursor: onClick ? "pointer" : "default",
        borderRadius: 2,
        border: `2px solid ${selected ? NURSE_COLORS.accent : NURSE_COLORS.border}`,
        bgcolor: selected ? NURSE_COLORS.accentSoft : "#fff",
        transition: "all .15s ease",
        "&:hover": onClick
          ? { borderColor: NURSE_COLORS.accent, transform: "translateY(-1px)", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }
          : undefined,
      }}
    >
      <Stack direction="row" spacing={1.5} sx={{ alignItems: "center" }}>
        <Avatar sx={{ bgcolor: NURSE_COLORS.primary, width: 40, height: 40, fontSize: 14 }}>
          {initials(patient.name)}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" noWrap>
            {patient.name || "Unknown"}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {[patient.uhid, patient.age != null ? `${patient.age}${patient.gender || ""}` : null, patient.consultant]
              .filter(Boolean)
              .join(" · ")}
          </Typography>
        </Box>
        {pendingTasks > 0 && (
          <Chip
            icon={<WarningAmberIcon sx={{ fontSize: 14 }} />}
            size="small"
            label={pendingTasks}
            color="warning"
          />
        )}
      </Stack>
      <Stack direction="row" spacing={1} useFlexGap sx={{ mt: 1.5, flexWrap: "wrap" }}>
        <Chip
          size="small"
          icon={<HotelIcon sx={{ fontSize: 14 }} />}
          label={[patient.wardName || patient.ward, patient.room && patient.bed ? `${patient.room}/${patient.bed}` : patient.bed]
            .filter(Boolean)
            .join(" · ") || "Bed —"}
        />
        {patient.fallRisk && (
          <Chip
            size="small"
            icon={<PersonIcon sx={{ fontSize: 14 }} />}
            label={`Fall: ${patient.fallRisk}`}
            color={riskColor(patient.fallRisk)}
            variant="outlined"
          />
        )}
        {patient.painScore != null && (
          <Chip size="small" label={`Pain ${patient.painScore}/10`} variant="outlined" />
        )}
      </Stack>
      {patient.diagnosis && (
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
          Dx: {patient.diagnosis}
        </Typography>
      )}
    </Paper>
  );
}
